import {
    Card,
    CardBody,
    Typography,
    Input,
    IconButton,
    Avatar,
} from "@material-tailwind/react";
import {
    ArrowLongUpIcon,
    ArrowLongDownIcon,
    PencilSquareIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon
} from "@heroicons/react/24/outline";
import {PlusCircleIcon} from "@heroicons/react/24/solid";
import React, {useEffect, useState, useMemo} from "react";
import getData from "../../api/axiosInstance";
import axios from "axios";
import {useTheme} from "../common/ThemeContext";

// Dialogs
import AddRefundDialog from "./dialogs/AddRefundDialog";
import EditRefundDialog from "./dialogs/EditRefundDialog";

export default function RefundsDashboard() {

    // state management
    const [refundsList, setRefundsList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editRefundId, setEditRefundId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { isDark } = useTheme();

    // sort state (server-side)
    const [sortSettings, setSortSettings] = useState({
        key: 'refundDate',
        direction: 'desc'
    });

    // pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const hasPages = totalPages > 0;
    const displayPage = hasPages ? currentPage + 1 : 0;
    const isFirstPageDisabled = currentPage <= 0;
    const isLastPageDisabled = !hasPages || currentPage >= totalPages - 1;

    // status filter
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    // api configuration
    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => {
        return {
            'Authorization': `Bearer ${token}`
        };
    }, [token]);

    // event handlers
    const toggleEditDialog = (refund) => {
        setEditRefundId(refund.id);
        setIsEditDialogOpen(true);
    };

    const handleSort = (field) => {
        setSortSettings(prev => {
            if (prev.key === field) {
                return {
                    key: field,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            } else {
                return {
                    key: field,
                    direction: 'asc'
                };
            }
        });
        setCurrentPage(0);
    };

    // pagination handlers
    const handleFirstPage = () => {
        if (!hasPages) return;
        setCurrentPage(0);
    };

    const handlePrevPage = () => {
        if (!hasPages) return;
        setCurrentPage(prev => Math.max(0, prev - 1));
    };

    const handleNextPage = () => {
        if (!hasPages) return;
        setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    };

    const handleLastPage = () => {
        if (!hasPages) return;
        setCurrentPage(totalPages - 1);
    };

    // refresh helpers
    const buildParams = (page) => {
        return new URLSearchParams({
            page: page.toString(),
            size: itemsPerPage.toString(),
            sortField: sortSettings.key,
            sortDirection: sortSettings.direction,
            ...(searchTerm && { searchTerm }),
            ...(selectedStatuses.length > 0 && { statuses: selectedStatuses.join(',') }),
        });
    };

    const applyPageData = (data) => {
        setRefundsList(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
    };

    const refreshList = async () => {
        setCurrentPage(0);
        setIsLoading(true);

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        try {
            const response = await axios.get(
                `${API_URL}/refunded_items/all?${buildParams(0).toString()}`,
                { headers: requestHeaders }
            );
            applyPageData(response.data);
        } catch (err) {
            console.error("Error refreshing:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const refreshCurrentPage = async () => {
        setIsLoading(true);

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        try {
            const response = await axios.get(
                `${API_URL}/refunded_items/all?${buildParams(currentPage).toString()}`,
                { headers: requestHeaders }
            );
            applyPageData(response.data);
        } catch (err) {
            console.error("Error refreshing:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // side effects
    useEffect(() => {
        setIsLoading(true);

        getData(
            `/refunded_items/all?${buildParams(currentPage).toString()}`,
            requestHeaders,
            (data) => {
                applyPageData(data);
                setIsLoading(false);
            },
            "Error fetching refunds data"
        );
    }, [requestHeaders, currentPage, itemsPerPage, searchTerm, selectedStatuses, sortSettings]);

    // Sort icon helper
    const SortIcon = ({ field }) => {
        if (sortSettings.key !== field) return null;
        return sortSettings.direction === 'desc'
            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />;
    };

    return (
        <Card className="w-full shadow-[0_0_15px_rgba(0,0,0,0.1)] relative overflow-hidden dark:bg-dark-900">
            <CardBody className="relative">
                <Typography variant="h5" color="blue-gray" className="mb-4 flex items-center justify-between dark:text-gray-300">
                    Refunds
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsAddDialogOpen(true)}
                            className="mv-primary-button"
                        >
                            <PlusCircleIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Add</span>
                        </button>
                    </div>
                </Typography>
                <hr className="my-2 border-blue-gray-50 dark:border-gray-700" />

                <div
                    className={`relative overflow-x-auto h-[calc(100vh-24rem)] ${
                        isLoading ? 'overflow-x-hidden' : ''
                    } scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200
                    dark:scrollbar-thumb-gray-400 dark:scrollbar-track-dark-800`}
                >
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="sticky top-0 bg-gray-50 z-10 dark:bg-dark-800">
                        <tr>
                            <th
                                className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[150px]"
                                onClick={() => handleSort('title')}
                            >
                                Name
                                <SortIcon field="title" />
                            </th>
                            <th
                                className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[115px]"
                                onClick={() => handleSort('status')}
                            >
                                Status
                                <SortIcon field="status" />
                            </th>
                            <th
                                className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                onClick={() => handleSort('paidPrice')}
                            >
                                Paid Price
                                <SortIcon field="paidPrice" />
                            </th>
                            <th
                                className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                onClick={() => handleSort('refundedAmount')}
                            >
                                Refunded Amount
                                <SortIcon field="refundedAmount" />
                            </th>
                            <th
                                className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                onClick={() => handleSort('refundDate')}
                            >
                                Refund Date
                                <SortIcon field="refundDate" />
                            </th>
                        </tr>
                        </thead>

                        {isLoading ? (
                            <tbody className="bg-white dark:bg-dark-900 h-[calc(100vh-24rem)]">
                            <tr>
                                <td colSpan={5} className="px-4 py-1.5">
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            <div className="relative w-24 h-24">
                                                <img
                                                    src="/images/arcado-logo-solo-circle.png"
                                                    alt="Arcado Logo"
                                                    className="w-full h-full object-contain animate-[pulse_1.5s_ease-in-out_infinite]"
                                                />
                                                <div className="absolute inset-0 rounded-full border-4 border-cyan-900 dark:border-cyan-200 border-opacity-30 animate-[ping_1.5s_ease-in-out_infinite]"></div>
                                            </div>
                                            <div className="text-center">
                                                <Typography variant="h6" className="text-gray-800 dark:text-gray-500 font-bold">
                                                    Loading refunds...
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        ) : (
                            <tbody className="bg-white divide-y dark:bg-dark-900 divide-gray-300 dark:divide-gray-700">
                            {refundsList.map((refund) => (
                                <tr key={refund.id}>
                                    <td className="px-4 py-1.5 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <PencilSquareIcon
                                                onClick={() => toggleEditDialog(refund)}
                                                className="w-4 h-4 text-cyan-900 dark:text-cyan-200 cursor-pointer mr-2"
                                            />
                                            <div className="flex-shrink-0 w-7 h-7">
                                                <Avatar className="w-6 h-6 rounded-full" src={refund.purchasedItem.imageUrl} alt="User" />
                                            </div>
                                            <div className="ml-4 relative overflow-hidden max-w-[280px] group">
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-300 truncate group-hover:whitespace-normal group-hover:overflow-visible group-hover:max-w-none">
                                                    {refund.purchasedItem.title}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-600">
                                                    By {refund.purchasedItem.author}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-1.5 whitespace-nowrap">
                                        <div className={`text-center inline-flex px-2 text-xs font-semibold rounded-md cursor-pointer
                                            ${refund.status === "ISSUED" ?
                                            "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" : ""}
                                            ${refund.status === "COMPLETED" ?
                                            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}
                                            ${refund.status === "CANCELED" ?
                                            "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" : ""}
                                            border border-gray-300 dark:border-gray-600`}>
                                            {refund.status === "ISSUED" && "Issued"}
                                            {refund.status === "COMPLETED" && "Completed"}
                                            {refund.status === "CANCELED" && "Canceled"}
                                        </div>
                                    </td>
                                    <td className="px-4 py-1.5 whitespace-nowrap">
                                        <p className="text-sm text-gray-900 dark:text-gray-300">
                                            {Number.isFinite(refund.paidPrice)
                                                ? refund.paidPrice.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-4 py-1.5 whitespace-nowrap">
                                        <p className="text-sm text-gray-900 dark:text-gray-300 font-bold">
                                            {Number.isFinite(refund.refundedAmount)
                                                ? refund.refundedAmount.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '/'}
                                        </p>
                                    </td>
                                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                        {refund.refundDate ?
                                            new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(refund.refundDate))
                                            : '/'
                                        }
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="mt-4 mb-4 flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
                    {/* LEVA KOLONA - Items per page */}
                    <div className="flex items-center gap-2 justify-center md:justify-start">
                        <Typography
                            variant="small"
                            className="text-gray-600 dark:text-gray-400 whitespace-nowrap"
                        >
                            Items per page:
                        </Typography>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(parseInt(e.target.value, 10));
                                setCurrentPage(0);
                            }}
                            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900
                                rounded-md px-2 py-1 text-sm w-18
                                focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                        </select>
                    </div>

                    {/* SREDINA */}
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing page {displayPage} of {totalPages} ({totalElements} total items)
                    </div>

                    {/* DESNA KOLONA */}
                    <div className="flex justify-center items-center gap-2">
                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handleFirstPage}
                            disabled={isFirstPageDisabled}
                            className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronDoubleLeftIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handlePrevPage}
                            disabled={isFirstPageDisabled}
                            className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronLeftIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                        <Typography className="dark:text-gray-400 whitespace-nowrap">
                            <strong className="text-gray-900 dark:text-gray-300">{displayPage}</strong>{" "}
                            of{" "}
                            <strong className="text-gray-900 dark:text-gray-300">{totalPages}</strong>
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handleNextPage}
                            disabled={isLastPageDisabled}
                            className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronRightIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handleLastPage}
                            disabled={isLastPageDisabled}
                            className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronDoubleRightIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                    </div>
                </div>

                {/* Search */}
                <div className="flex items-center mt-5 w-full gap-2 md:w-max">
                    <div className="relative">
                        <Input
                            type="search"
                            variant="outlined"
                            label="Title"
                            placeholder="Search"
                            containerProps={{
                                className: "min-w-full sm:min-w-[288px]",
                            }}
                            className="placeholder:text-blue-gray-300 dark:bg-dark-800"
                            color={isDark ? "white" : ""}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(0);
                            }}
                        />
                    </div>
                </div>

                {/* Dialogs */}
                <AddRefundDialog
                    isOpen={isAddDialogOpen}
                    onClose={() => setIsAddDialogOpen(false)}
                    onSuccess={refreshList}
                    requestHeaders={requestHeaders}
                />

                <EditRefundDialog
                    isOpen={isEditDialogOpen}
                    onClose={() => setIsEditDialogOpen(false)}
                    onSuccess={refreshCurrentPage}
                    requestHeaders={requestHeaders}
                    refundId={editRefundId}
                />
            </CardBody>
        </Card>
    );
}
